import { Alert } from 'src/design/atoms/Alert';
import { Anchor } from 'src/design/atoms/Anchor';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconAlertTriangle, IconGitFork } from '@tabler/icons-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useChat } from 'src/hooks/useChat';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Composer } from 'src/app/chat/Composer';
import { MessageList } from 'src/app/chat/MessageList';
import { RetentionNotice } from 'src/app/chat/RetentionNotice';
import { UsageMeter } from 'src/app/chat/UsageMeter';

export interface IChatPanelProps {
  composerRef?: React.Ref<HTMLTextAreaElement>;
  onRefClick?: () => void;
}

/**
 * The shared chat surface (messages + composer) reused by the drawer and the full assistant page.
 * @registry Full chat surface (message list, error banner, composer, usage meter); wraps useChat internally, no props required beyond an optional composer ref.
 */
export const ChatPanel: React.FC<IChatPanelProps> = ({
  composerRef,
  onRefClick,
}) => {
  const {
    messages,
    loadingConversation,
    streaming,
    busy,
    error,
    usage,
    conversationId,
    conversations,
    composerDraft,
    consumeComposerDraft,
    thinkHarder,
    setThinkHarder,
    sendMessage,
    retryLast,
    stop,
    selectConversation,
  } = useChat();

  const activeConversation = conversations.find((c) => c.id === conversationId);
  const forkedFromId = activeConversation?.forkedFromConversationId;

  const innerRef = useRef<HTMLTextAreaElement>(null);

  const mergedRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof composerRef === 'function') {
        composerRef(node);
      } else if (composerRef) {
        (
          composerRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = node;
      }
    },
    [composerRef],
  );

  // Autofocus the composer whenever the active conversation changes (new chat,
  // an existing conversation finishing its load, or on initial mount).
  useEffect(() => {
    if (loadingConversation) return;
    const id = setTimeout(() => innerRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [conversationId, loadingConversation]);

  return (
    <Stack gap={0} h="100%" style={{ minHeight: 0 }}>
      {forkedFromId && (
        <>
          <Group
            gap={4}
            wrap="nowrap"
            px="sm"
            py={4}
            data-testid="forked-from"
            style={{ flex: '0 0 auto', minWidth: 0 }}
          >
            <IconGitFork
              size={14}
              style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }}
            />
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
              Forked from
            </Text>
            <Anchor
              component="button"
              type="button"
              size="xs"
              fw={500}
              truncate
              style={{ minWidth: 0 }}
              onClick={() => void selectConversation(forkedFromId)}
            >
              {activeConversation?.forkedFromTitle || 'New conversation'}
            </Anchor>
          </Group>
          <Divider />
        </>
      )}
      <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        <MessageList
          messages={messages}
          loading={loadingConversation}
          onRefClick={onRefClick}
        />
      </div>
      {error &&
        (() => {
          const isUpstream = error.code === 'upstream_unavailable';
          return (
            <Alert
              color={isUpstream ? 'yellow' : 'red'}
              icon={isUpstream ? <IconAlertTriangle size={16} /> : undefined}
              mx="sm"
              mb="xs"
              title={
                isUpstream
                  ? 'AI service temporarily unavailable'
                  : 'Something went wrong'
              }
            >
              <Group
                justify="space-between"
                align="center"
                wrap="nowrap"
                gap="sm"
              >
                <span>
                  {error.message}
                  {isUpstream &&
                    error.retryAfterSeconds != null &&
                    ` You can retry in about ${error.retryAfterSeconds} seconds.`}
                </span>
                <BasicButton
                  variant="light"
                  color={isUpstream ? undefined : 'red'}
                  size="xs"
                  onClick={retryLast}
                >
                  Retry
                </BasicButton>
              </Group>
            </Alert>
          );
        })()}
      <Divider />
      <Composer
        ref={mergedRef}
        streaming={streaming}
        busy={busy}
        onSend={sendMessage}
        onStop={stop}
        draft={composerDraft}
        onDraftApplied={consumeComposerDraft}
        thinkHarder={thinkHarder}
        onThinkHarderChange={setThinkHarder}
      />
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        gap="xs"
        px="sm"
        pt={4}
        pb="xs"
        style={{ flex: '0 0 auto' }}
      >
        <UsageMeter usage={usage} />
        <RetentionNotice />
      </Group>
    </Stack>
  );
};
